import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { cn } from './cnNew'

/**
 * Popover accesible sin dependencias: cierre por click fuera / Escape,
 * y alineación configurable respecto al disparador.
 *
 * Uso:
 *   <Popover renderTrigger={({ open, toggle, props }) => <button {...props} onClick={toggle} />}>
 *     {({ close }) => <div>contenido</div>}
 *   </Popover>
 */
const MARGIN = 12
const MIN_HEIGHT = 220

export function Popover({
  renderTrigger,
  children,
  align = 'start',
  width = 280,
  className,
  contentClassName,
  onOpenChange,
}) {
  const [open, setOpen] = useState(false)
  const [flipUp, setFlipUp] = useState(false)
  const [maxHeight, setMaxHeight] = useState(null)
  const wrapperRef = useRef(null)
  const contentRef = useRef(null)
  const id = useId()

  const change = useCallback(
    (next) => {
      setOpen(next)
      if (onOpenChange) onOpenChange(next)
    },
    [onOpenChange]
  )

  const close = useCallback(() => change(false), [change])
  const toggle = useCallback(() => change(!open), [change, open])

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) close()
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  // Elige el lado con más espacio y limita la altura al hueco disponible, de
  // modo que el contenido siempre sea accesible con scroll interno. Se
  // recalcula si el contenido crece (p. ej. al abrir la condición personalizada).
  useEffect(() => {
    if (!open) return undefined
    const trigger = wrapperRef.current
    const content = contentRef.current
    if (!trigger || !content) return undefined

    const measure = () => {
      const triggerRect = trigger.getBoundingClientRect()
      const spaceBelow = window.innerHeight - triggerRect.bottom - MARGIN
      const spaceAbove = triggerRect.top - MARGIN
      const needed = content.scrollHeight
      const up = needed > spaceBelow && spaceAbove > spaceBelow

      setFlipUp(up)
      setMaxHeight(Math.max(MIN_HEIGHT, Math.floor(up ? spaceAbove : spaceBelow)))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open])

  const alignment =
    align === 'end' ? 'right-0' : align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0'

  return (
    <div ref={wrapperRef} className={cn('relative inline-flex', className)}>
      {renderTrigger({
        open,
        toggle,
        close,
        props: {
          'aria-expanded': open,
          'aria-haspopup': 'dialog',
          'aria-controls': open ? id : undefined,
          'data-state': open ? 'open' : 'closed',
        },
      })}

      {open && (
        <div
          ref={contentRef}
          id={id}
          role="dialog"
          style={{
            width: typeof width === 'number' ? `${width}px` : width,
            maxHeight: maxHeight ? `${maxHeight}px` : undefined,
          }}
          className={cn(
            'scrollbar-slim absolute z-50 overflow-y-auto rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-[0_12px_32px_rgba(16,42,48,0.14)]',
            alignment,
            flipUp ? 'bottom-full mb-2' : 'top-full mt-2',
            contentClassName
          )}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      )}
    </div>
  )
}
